import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, CartesianGrid, LabelList,
} from "recharts";
import {
  DollarSign, AlertTriangle, Clock, ReceiptText, ArrowRight, Building2, Globe,
} from "lucide-react";
import api from "@/lib/api";
import { bucketBadgeClass } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const inr = (n) => `₹${Number(Math.round(n || 0)).toLocaleString("en-IN")}`;

const Kpi = ({ icon: Icon, label, value, sub, tone, testid }) => (
  <Card className={`p-5 lg:p-6 pt-fade-up border-border/80`} data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-heading text-xl xl:text-lg 2xl:text-2xl font-bold mt-2 whitespace-nowrap tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tone}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    const [d, f] = await Promise.all([api.get("/dashboard"), api.get("/reports/incoming-monthwise")]);
    setData(d.data);
    setForecast(f.data);
  };
  useEffect(() => { load(); }, []);

  if (!data)
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
      </div>
    );

  const empty = data.total_invoices === 0;
  const chartData = (forecast?.rows || []).slice(0, 4).map((r) => ({ label: r.label, total: r.amount_inr }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Receivables Overview</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Outstanding balances, ageing risk and collection health.
          </p>
        </div>
      </div>

      {empty ? (
        <Card className="p-12 text-center border-dashed">
          <ReceiptText className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading text-lg font-semibold">No receivables yet</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-5 max-w-md mx-auto">
            Import your domestic and export Excel files, or configure Dropbox auto-sync, to populate the dashboard.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={() => nav("/import")}>Import Excel</Button>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6">
            <Kpi testid="dashboard-total-outstanding" icon={DollarSign} label="Total Outstanding"
                 value={inr(data.total_outstanding_inr)}
                 sub={`${data.total_invoices} open invoices · in INR`}
                 tone="bg-primary/10 text-primary" />
            <Kpi testid="dashboard-overdue-total" icon={AlertTriangle} label="Overdue"
                 value={inr(data.overdue_inr)}
                 sub={`${data.overdue_invoices} invoices past due`}
                 tone="bg-rose-500/10 text-rose-500" />
            <Kpi testid="dashboard-dso-metric" icon={Clock} label="Avg Days Overdue"
                 value={`${data.avg_days_overdue} d`}
                 sub="Across overdue invoices"
                 tone="bg-amber-500/10 text-amber-500" />
            <Kpi testid="dashboard-domestic-metric" icon={Building2} label="Domestic"
                 value={inr(data.domestic_total)}
                 sub="Domestic receivables · INR"
                 tone="bg-indigo-500/10 text-indigo-500" />
            <Kpi testid="dashboard-export-metric" icon={Globe} label="Export"
                 value={inr(data.export_total_inr)}
                 sub="Export receivables · in INR"
                 tone="bg-teal-500/10 text-teal-500" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
            <Card className="lg:col-span-2 p-5 lg:p-6" data-testid="forecast-chart">
              <h3 className="font-heading text-lg font-medium mb-1">Payment Overdue &amp; Expected</h3>
              <p className="text-xs text-muted-foreground mb-4">Overdue now, plus expected collections for the next 3 months · INR</p>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 28, left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false}
                         tickFormatter={(v) => (v >= 1e7 ? `${(v / 1e7).toFixed(1)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(0)}L` : v)} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }}
                    formatter={(v) => inr(v)} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                    <LabelList dataKey="total" position="top" offset={8}
                               formatter={(v) => `${(v / 1e7).toFixed(2)} Cr`}
                               style={{ fontSize: 11, fontWeight: 600, fill: "hsl(var(--foreground))" }} />
                    {chartData.map((e) => <Cell key={e.label} fill={e.label === "Overdue" ? "#EF4444" : "#6366F1"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {data.excluded_customers?.length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-3 border-t border-border/60 pt-3" data-testid="excluded-customers-note">
                  <span className="font-medium text-foreground">Excluded (Won't Pay):</span>{" "}
                  {data.excluded_customers.join(", ")}
                </p>
              )}
            </Card>

            <Card className="p-5 lg:p-6" data-testid="at-risk-widget">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-medium">Top At-Risk</h3>
                <Button variant="ghost" size="sm" onClick={() => nav("/customers")} className="text-xs">
                  All <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <div className="space-y-3">
                {data.top_at_risk.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.customer_name}</p>
                      <Badge variant="outline" className={`mt-1 text-[10px] ${bucketBadgeClass(c.max_days <= 30 ? "0-30" : c.max_days <= 60 ? "31-60" : c.max_days <= 90 ? "61-90" : c.max_days <= 180 ? "91-180" : "180+")}`}>
                        {c.max_days}d overdue
                      </Badge>
                    </div>
                    <span className="text-sm font-mono font-semibold shrink-0">{c.amount_display}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
